import _jsx from "@babel/runtime/helpers/jsx";
var _OneClickHeading, _VHRLeadHeading;
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import React, { useContext, lazy, Suspense, useCallback, useEffect } from 'react';
import { useFeatures } from '@atc/features';
import { formatCurrency } from '@atc/string-fns';
import { Input, StandardEmailInput, StandardPhoneInput, Textarea, validationRules } from 'reaxl-forms';
import { sendClick } from 'reaxl-analytics';
import { smsOptClick } from 'reaxl-analytics-handlers';
import EmailContext from '../EmailContext';
import EmailStaticContent from '../EmailStaticContent';
import InquirySelections from '../InquirySelections';
import OneClickHeading from '../OneClickHeading';
import OneClickOptIn from '../OneClickOptIn';
import VHRLeadHeading from '../VHRLeadHeading';
const EmailFormCalendarScheduler = /*#__PURE__*/lazy(() => import(/* webpackChunkName: "EmailFormCalendarScheduler" */
'../EmailFormCalendarScheduler'));
const appliedForCreditDate = function (creditApplications, ownerId) {
  if (creditApplications === void 0) {
    creditApplications = [];
  }
  let creditApplicationDate = '';
  creditApplications.forEach(creditApplication => {
    if (creditApplication?.dealId?.length > 0 && ownerId === creditApplication?.ownerId) {
      creditApplicationDate = creditApplication.submittedOn;
    }
  });
  return creditApplicationDate;
};
const buildMessage = _ref => {
  let {
    listing,
    brandName,
    uiContext,
    inquiryProps,
    ctaContext,
    isEditEmailModal,
    isOneClickChecked
  } = _ref;
  const {
    id,
    title,
    listingTitle,
    pricingDetail = {},
    owner
  } = listing;
  if (id) {
    const vehicleTitle = title || listingTitle || 'vehicle';
    const price = pricingDetail.incentive || pricingDetail.salePrice || pricingDetail.msrp || 0;
    const priceClause = price ? ` for ${formatCurrency(price)}` : '';
    if (isEditEmailModal || isOneClickChecked && uiContext !== 'multi') {
      return 'Please confirm vehicle availability.';
    }
    switch (uiContext) {
      case 'buildAndPrice':
        return `I would like to get a quote for a ${vehicleTitle}. Please contact me to discuss the options, details and price for this vehicle.`;
      case 'wallet':
        return `I'd like to follow up on my deal for the ${vehicleTitle}.`;
      case 'walletCreditApp':
        return `I'd like to follow up on my credit application for the ${vehicleTitle}.`;
      case 'multi':
        return 'I would like more information about this vehicle.';
      default:
    }
    if (ctaContext) {
      if (ctaContext?.includes('longHaul')) {
        return `I'd like to receive more delivery information for the ${vehicleTitle} listed${priceClause}.`;
      }
      switch (ctaContext) {
        case 'quote':
          return `I would like to get a price for a ${vehicleTitle} with these specified features. Please contact me with pricing and available options on this vehicle.`;
        case 'creditStatus':
          return `I'd like to receive status update of my Credit Application submitted on ${appliedForCreditDate(inquiryProps?.creditApplications ?? [], owner?.id)}`;
        case 'walletCreditApp':
          return `I'd like to follow up on my credit application for the ${vehicleTitle}.`;
        case 'availableOffers':
          return `I'm interested in discussing the price of this ${vehicleTitle}.`;
        case 'calculator':
        case 'calculatorOfferSummary':
          return `I want to make an offer on your ${vehicleTitle} listed${priceClause}.`;
        case 'calculatorDealSubmitted':
          return `I want to update my offer on your ${vehicleTitle} listed${priceClause}.`;
        case 'calculatorLeaseError':
          return `I'd like more information on leasing your ${vehicleTitle} listed${priceClause}.`;
        case 'incentives':
          return `I would like more information about available offers for the ${vehicleTitle}.`;
        default:
      }
    }
    if (inquiryProps.inquirySelection) {
      switch (inquiryProps.inquirySelection) {
        case 'confirmAvailability':
          return `Is your ${vehicleTitle} listed${priceClause} still available?`;
        case 'information':
          return `I'd like to receive more information about your ${vehicleTitle} listed${priceClause}.`;
        case 'testDrive':
          return `I'd like to test drive your ${vehicleTitle} listed${priceClause}.`;
        case 'virtualTour':
          return `I'd like to have a video walkthrough of your ${vehicleTitle} listed${priceClause}.`;
        case 'vehicleHistory':
          return `I'd like to receive a copy of the Vehicle History Report for your ${vehicleTitle}.`;
        default:
      }
    }
    return `I'm interested in your ${vehicleTitle} listed${priceClause}.`;
  }
  return `Hi, I discovered your dealership on ${brandName} and would like to receive more information.`;
};

/**
 *  EmailFormBody is the component that renders email form inputs and textarea
 *  and hosts flat form logic
 */
function EmailFormBody(_ref2) {
  let {
    userProfile,
    ctaContext = '',
    inquiryProps = {},
    isEditEmailModal = false,
    isOneClickChecked = false,
    setOneClickChecked = () => {},
    showInquiriesOnly,
    userDuck,
    enableSMSOptInDisplay = () => {},
    handleEmailInputInteraction = () => {},
    leadProfile,
    isLoggedIn
  } = _ref2;
  validationRules();
  const dispatch = useDispatch();
  const {
    brand: [, {
      brand_name: brandName
    }],
    one_click_lead_vdp: [enableOneClickVDP]
  } = useFeatures(['brand', 'one_click_lead_vdp']);
  const {
    listing = {},
    owner = {},
    uiContext
  } = useContext(EmailContext);
  const isCalculator = ['calculator', 'calculatorOfferSummary', 'calculatorBreakdownConfirmation'].includes(uiContext);
  const isOneClickAvailable = uiContext === 'edit' || uiContext === 'default' && enableOneClickVDP;
  const isEditEmailOnly = isOneClickAvailable && isEditEmailModal;
  const isVHRLead = uiContext === 'vehicleHistoryReport';
  const {
    firstName = '',
    lastName = '',
    email = '',
    phone = ''
  } = userProfile ?? {};
  const {
    inquiry: inquiryConfig,
    validationMessages
  } = EmailStaticContent;
  const hasSchedule = inquiryConfig[inquiryProps.inquirySelection]?.schedule && ctaContext !== 'calculatorDealSubmitted';
  const handleOneClickCheck = useCallback(event => {
    setOneClickChecked(event?.currentTarget?.checked);
  }, [setOneClickChecked]);

  // This useEffect checks whether we should display the sms checkbox on page load.
  useEffect(() => {
    if (leadProfile?.isOneClickOptedIn) {
      enableSMSOptInDisplay(false);
    } else if (phone && !owner?.hasTCPA && uiContext === 'default') {
      enableSMSOptInDisplay(true);
      /**
       * Tagging should fire when
       *  1. on page load with uiContext = 'default' and lead form has phoneNumber
       *  2. User change the different phoneNumber
       */
      sendClick(smsOptClick, null, {
        checked: true
      });
    }
  }, [leadProfile?.isOneClickOptedIn, enableSMSOptInDisplay, phone, owner?.hasTCPA, uiContext]);
  const handleBlur = event => {
    const inputNameMapToUserProfile = {
      firstName: 'firstName',
      lastName: 'lastName',
      fromEmailAddress: 'email',
      phoneNumber: 'phone'
    };
    if (event.target.value && event.target.getAttribute('isvalid') === 'true') {
      const userProfileAttr = inputNameMapToUserProfile[event.target.name];
      userProfile[userProfileAttr] = event.target.value;
    }
    if (event?.target?.name === 'phoneNumber' && typeof enableSMSOptInDisplay === 'function') {
      if (event?.target?.getAttribute('isvalid') === 'true' && event?.target?.value !== '' && !owner?.hasTCPA) {
        enableSMSOptInDisplay(true);
      }
      if (event?.target?.getAttribute('isvalid') === 'false' || event?.target?.value === '') {
        enableSMSOptInDisplay(false);
      }
    }
    if (userDuck && (userProfile.firstName || userProfile.lastName) && (userProfile.phone || userProfile.email)) {
      dispatch(userDuck.creators.setLeadProfile(userProfile, true));
      if (isLoggedIn) {
        dispatch(userDuck.creators.setUserEditProfile());
      }
    }
  };
  const handleInputFieldClick = e => {
    handleEmailInputInteraction(e, ctaContext || uiContext);
  };
  const handlePhoneInputChange = event => {
    const cleanedPhone = event?.target?.value.replace(/\D/g, '');
    const isPhoneInputValid = /^\d{10}$/.test(cleanedPhone);
    if (!isPhoneInputValid) {
      enableSMSOptInDisplay(false);
    } else if (!owner?.hasTCPA) {
      enableSMSOptInDisplay(true);
    }
  };
  const userInputs = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx("div", {
    className: "row"
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "col-xs-6"
  }, void 0, /*#__PURE__*/_jsx(Input, {
    label: "First Name",
    maxLength: "20",
    name: "firstName",
    autoComplete: "given-name",
    validations: validationMessages.name,
    validationErrors: {
      isValidName: `First Name ${validationMessages.hasInvalidChars}`,
      isDefaultRequiredValue: validationMessages.enterFirst,
      isNotProfane: validationMessages.isProfane
    },
    required: true,
    value: firstName,
    layout: "inside",
    onBlur: handleBlur
  })), /*#__PURE__*/_jsx("div", {
    className: "col-xs-6"
  }, void 0, /*#__PURE__*/_jsx(Input, {
    maxLength: "20",
    name: "lastName",
    label: "Last Name",
    autoComplete: "family-name",
    validations: validationMessages.name,
    validationErrors: {
      isValidName: `Last Name ${validationMessages.hasInvalidChars}`,
      isDefaultRequiredValue: validationMessages.enterLast,
      isNotProfane: validationMessages.isProfane
    },
    required: true,
    value: lastName,
    layout: "inside",
    onBlur: handleBlur
  }))), /*#__PURE__*/_jsx("div", {
    className: "row"
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "col-xs-6"
  }, void 0, /*#__PURE__*/_jsx(StandardEmailInput, {
    maxLength: "45",
    name: "fromEmailAddress",
    autoComplete: "email",
    required: true,
    value: email,
    layout: "inside",
    onBlur: handleBlur,
    onClick: handleInputFieldClick
  })), /*#__PURE__*/_jsx("div", {
    className: "col-xs-6"
  }, void 0, /*#__PURE__*/_jsx(StandardPhoneInput, {
    name: "phoneNumber",
    optional: true // optional attribute is used to set orientation and not form field validation, i.e add's 'Optional' text next to Phone label on UI
    ,
    onChange: handlePhoneInputChange,
    value: phone,
    validateOn: "blur",
    autoComplete: "tel",
    layout: "inside",
    onBlur: handleBlur
  }))));
  const textarea = /*#__PURE__*/_jsx(Textarea, {
    disabled: uiContext !== 'multi' && isEditEmailModal || isOneClickChecked && (uiContext === 'default' || uiContext === 'edit'),
    maxLength: "250",
    name: "comments",
    label: "Message",
    rows: 3,
    placeholder: isCalculator ? '' : 'Enter your message here. Maximum 250 characters.',
    validations: "isWordsAndSomePunctuation,isNotProfane",
    validationErrors: validationRules.messageValidations,
    resizable: true,
    required: !isCalculator,
    optional: isCalculator,
    value: buildMessage({
      listing,
      brandName,
      uiContext,
      inquiryProps,
      ctaContext,
      isEditEmailModal,
      isOneClickChecked
    }),
    layout: "inside"
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InquirySelections, inquiryProps), !showInquiriesOnly && /*#__PURE__*/React.createElement(React.Fragment, null, hasSchedule && /*#__PURE__*/_jsx(Suspense, {
    fallback: null
  }, void 0, /*#__PURE__*/_jsx(EmailFormCalendarScheduler, {
    owner: owner,
    layout: "inside"
  })), isEditEmailOnly && (_OneClickHeading || (_OneClickHeading = /*#__PURE__*/_jsx(OneClickHeading, {}))), isVHRLead && (_VHRLeadHeading || (_VHRLeadHeading = /*#__PURE__*/_jsx(VHRLeadHeading, {}))), userInputs, !isVHRLead && textarea, isOneClickAvailable && /*#__PURE__*/_jsx(OneClickOptIn, {
    listingId: listing?.id,
    handleOneClickCheck: handleOneClickCheck,
    isOneClickChecked: isOneClickChecked
  })));
}
export default EmailFormBody;